import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { INVOICE_STATUSES } from "constants/statuses";
import { relativeOrDateTime, toDollar } from "utils";

import {
  useInvoiceDownload,
  useInvoices,
  useCurrentUserCompany,
} from "hooks/api";
import { useReferrerState } from "hooks";
import { useSnackbar } from "contexts";
import {
  InfoIcon,
  DownloadIcon,
  ReceiptTextIcon,
  ShoppingBagIcon,
} from "lucide-react";
import { ROUTES } from "routes";

import type { GridRowSelectionModel } from "@mui/x-data-grid-pro";

import { Button } from "@repo/ui";
import {
  EmptyState,
  Table,
  LinkCell,
  StatusChipCell,
  CategoryCell,
  ActionDropdown,
  UserCell,
  CompanyBlock,
  PaymentCell,
} from "components";
import { Stack } from "@mui/material";
import { AddressPreview } from "components/display/AddressPreview";
import { headerWithTerm } from "components/display/Table";
import { statusFilterOptions } from "components/input/filters/StatusChipFilter";

export const Invoices = () => {
  const navigate = useNavigate();
  const { open: notify } = useSnackbar();
  // Referrer state handed to the invoice drill-in so its Header offers "← Back to Billing".
  const referrer = useReferrerState();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [pageCursors, setPageCursors] = useState<
    Record<number, string | undefined>
  >({
    0: undefined,
  });
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const queryParams = useMemo(
    () => ({
      limit: paginationModel.pageSize,
      startingAfter:
        paginationModel.page === 0
          ? undefined
          : pageCursors[paginationModel.page],
    }),
    [paginationModel, pageCursors]
  );

  const { data, isLoading, isRefetching } = useInvoices(queryParams);
  const { data: currentCompany } = useCurrentUserCompany();
  const downloadInvoice = useInvoiceDownload();

  const invoices = data?.invoices ?? [];

  const rows = useMemo(
    () =>
      invoices.map((invoice) => ({
        id: invoice.id,
        number: invoice.number,
        created_at: invoice.createdAt,
        total: invoice.total ?? invoice.amountDue ?? 0,
        type: invoice.type,
        orderId: invoice.orderId,
        payment_status: invoice.status ?? "open",
        payment_method: invoice.paymentMethod,
        shipping_address: invoice.shipping,
        customerName: invoice.customerName,
        customerEmail: invoice.customerEmail,
        itemsCount: invoice.itemsCount,
      })),
    [invoices]
  );

  const handleViewInvoice = useCallback(
    (row) => {
      navigate(ROUTES.SETTINGS_COMPANY_INVOICE(row.id), { state: referrer });
    },
    [navigate, referrer]
  );

  const handleDownloadInvoice = useCallback(
    async (row) => {
      try {
        const { url } = await downloadInvoice.mutateAsync(row.id);
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (error) {
        notify({
          type: "error",
          text: "Unable to download invoice right now.",
        });
      }
    },
    [downloadInvoice, notify]
  );

  const getActionItems = useCallback(
    (row) => [
      {
        label: "Details",
        onClick: () => handleViewInvoice(row),
        icon: <InfoIcon strokeWidth={1} />,
      },
      {
        label: "Download PDF",
        onClick: () => handleDownloadInvoice(row),
        icon: <DownloadIcon strokeWidth={1} />,
      },
    ],
    [handleViewInvoice, handleDownloadInvoice]
  );

  /**
   * Bulk "Download PDFs".
   *
   * The URL is Stripe's own `invoice.invoice_pdf` (API `GET
   * billing/invoices/:id/download` returns it verbatim — see
   * `company-invoices.use-case.ts` `executeDownload`), so it is CROSS-ORIGIN.
   * That rules out the `<a download>` trick: the `download` attribute is ignored
   * for a cross-origin href, so the anchor would just navigate. Every invoice
   * therefore still needs its own window.
   *
   * What is fixed is the honesty. The old loop fired N `window.open` calls after
   * an `await` — outside the click's user-gesture window — so the popup blocker
   * silently dropped all but the first one or two, and the user was never told:
   * they believed they had twelve PDFs and had two.
   *
   * So: resolve every URL first (in parallel, so one bad invoice does not abort
   * the rest), then open them one at a time with a small gap, COUNT what the
   * browser actually gave us, and report it. `window.open` returns `null` when
   * blocked — which is why the `noopener,noreferrer` feature string is gone: it
   * makes the call return `null` on SUCCESS too, and there would be no way to
   * tell the two apart. `win.opener = null` severs the reference instead.
   */
  const handleBulkDownload = useCallback(
    async (selectedRows: Array<{ id: string }>) => {
      if (!selectedRows.length) return;

      const results = await Promise.all(
        selectedRows.map(async (row) => {
          try {
            const { url } = await downloadInvoice.mutateAsync(row.id);
            return url;
          } catch (error) {
            return null;
          }
        })
      );

      const urls = results.filter((url): url is string => !!url);
      const unavailable = results.length - urls.length;
      let opened = 0;

      for (const url of urls) {
        const win = window.open(url, "_blank");
        if (win) {
          try {
            win.opener = null;
          } catch {
            // Severing the opener is defence-in-depth, not the point of the
            // loop — never let it abort the rest of the batch.
          }
          opened += 1;
        }
        // Space the opens out. Back-to-back calls are what trips the blocker
        // hardest, and it also gives the user's "allow pop-ups" choice a chance
        // to apply to the rest of the batch.
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      const blocked = urls.length - opened;

      if (opened === results.length) {
        notify({
          type: "success",
          text:
            opened === 1
              ? "Downloading 1 invoice."
              : `Downloading ${opened} invoices.`,
        });
        return;
      }

      // Anything short of "all of them" says so explicitly, and says which of
      // the two failure modes it was — a blocked pop-up is fixed by the user,
      // a missing PDF is not.
      const reasons = [
        blocked > 0
          ? `${blocked} blocked by your browser's pop-up blocker (allow pop-ups for this site and try again)`
          : null,
        unavailable > 0 ? `${unavailable} had no PDF available` : null,
      ].filter(Boolean);

      notify({
        type: opened === 0 ? "error" : "warning",
        title:
          opened === 0
            ? "No invoices were downloaded"
            : `Downloaded ${opened} of ${results.length} invoices`,
        text: `${reasons.join(". ")}.`,
        persist: true,
      });
    },
    [downloadInvoice, notify]
  );

  const getBulkActions = useCallback(
    (selectedRows: Array<{ id: string }>) => [
      {
        label: "Download PDFs",
        onClick: () => handleBulkDownload(selectedRows),
        icon: <DownloadIcon strokeWidth={1} />,
      },
    ],
    [handleBulkDownload]
  );

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Invoice ID",
        // +18px for the info glyph headerWithTerm adds (§49: size for the HEADER). 190 was
        // already comfortable for "Invoice ID" plus a sort icon; measured at 208 the header
        // still renders in full with room to spare.
        renderHeader: headerWithTerm("Invoice ID", "invoice"),
        width: 208,
        pinned: "left",
        sortable: false,
        disableReorder: true,
        type: "string",
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }) => (
          <LinkCell
            align="left"
            link={`#${row.number ?? row.id}`}
            route={ROUTES.SETTINGS_COMPANY_INVOICE(row.id)}
          />
        ),
      },
      {
        field: "created_at",
        headerName: "Date",
        type: "string",
        width: 200,
        renderCell: ({ value }) => (value ? relativeOrDateTime(value) : "—"),
      },
      {
        field: "total",
        headerName: "Total",
        type: "number",
        width: 120,
        valueFormatter: (value) => toDollar(value ?? 0, true),
      },
      {
        field: "type",
        headerName: "Type",
        type: "multiSelect",
        width: 160,
        renderCell: ({ row }) => (
          <CategoryCell
            category={row.type ?? "other"}
            filter
            align="start"
            bordered={false}
          />
        ),
      },
      {
        field: "placed_by",
        headerName: "Placed by",
        type: "string",
        sortable: false,
        width: 240,
        // A tag *order* is placed by a person; a *subscription* charge is an
        // org-level recurring obligation placed by the Company (no individual
        // "places" it each cycle). Switch the renderer on invoice type.
        renderCell: ({ row }) =>
          row.type === "order" ? (
            <UserCell
              user={{
                first_name: row.customerName,
                email: row.customerEmail,
              }}
            />
          ) : (
            <Stack sx={{ py: 2 }} justifyContent="center" height="100%">
              <CompanyBlock company={currentCompany} />
            </Stack>
          ),
      },
      {
        field: "order",
        headerName: "Order",
        type: "string",
        width: 180,
        sortable: false,
        // Order-type invoices link out to their order; subscription invoices
        // have no order (renders "—").
        renderCell: ({ row }) =>
          row.orderId ? (
            <LinkCell
              link={`#${String(row.orderId).slice(0, 8)}`}
              route={ROUTES.TAGS_ORDER(row.orderId)}
            />
          ) : (
            "—"
          ),
      },
      {
        field: "payment_status",
        headerName: "Payment status",
        type: "statusChip",
        options: statusFilterOptions(INVOICE_STATUSES),
        width: 150,
        renderCell: ({ value }) => (
          <StatusChipCell
            status={value ?? "open"}
            statuses={INVOICE_STATUSES}
          />
        ),
      },
      {
        field: "payment_method",
        headerName: "Payment method",
        type: "string",
        width: 240,
        renderCell: ({ row }) =>
          row.payment_method ? (
            <PaymentCell paymentMethod={row.payment_method} link={false} />
          ) : (
            "—"
          ),
      },
      {
        field: "itemsCount",
        headerName: "Items",
        type: "number",
        width: 120,
        renderCell: ({ value }) =>
          value !== undefined && value !== null ? value : "—",
      },
      {
        field: "shipping_address",
        headerName: "Shipping address",
        type: "string",
        width: 300,
        renderCell: ({ row }) =>
          row.shipping_address ? (
            <AddressPreview address={row.shipping_address} />
          ) : (
            "—"
          ),
      },
      {
        field: "actions",
        headerName: false,
        pinned: "right",
        sortable: false,
        disableReorder: true,
        align: "center",
        width: 54,
        renderCell: ({ row }) => <ActionDropdown items={getActionItems(row)} />,
      },
    ],
    [getActionItems, currentCompany]
  );

  useEffect(() => {
    if (!data?.nextCursor) {
      return;
    }

    setPageCursors((prev) => {
      if (prev[paginationModel.page + 1] === data.nextCursor) {
        return prev;
      }

      return {
        ...prev,
        [paginationModel.page + 1]: data.nextCursor ?? undefined,
      };
    });
  }, [data?.nextCursor, paginationModel.page]);

  const handlePaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      const pageSizeChanged = model.pageSize !== paginationModel.pageSize;
      if (pageSizeChanged) {
        setPageCursors({ 0: undefined });
        setPaginationModel({ page: 0, pageSize: model.pageSize });
        return;
      }

      if (model.page > paginationModel.page) {
        if (!data?.hasMore) {
          return;
        }
        const cursorForNextPage =
          pageCursors[model.page] ?? data?.nextCursor ?? null;
        if (!cursorForNextPage) {
          return;
        }
        if (!pageCursors[model.page]) {
          setPageCursors((prev) => ({
            ...prev,
            [model.page]: cursorForNextPage,
          }));
        }
        setPaginationModel(model);
        return;
      }

      if (model.page < paginationModel.page) {
        setPaginationModel(model);
        return;
      }
    },
    [data?.hasMore, data?.nextCursor, pageCursors, paginationModel]
  );

  const estimatedRowCount =
    paginationModel.page * paginationModel.pageSize +
    rows.length +
    (data?.hasMore ? paginationModel.pageSize : 0);

  return (
    <Table
      fillBottom
      loading={isLoading || isRefetching}
      topOffset={336}
      columns={columns}
      rows={rows}
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      rowCount={estimatedRowCount}
      bulkActions={getBulkActions}
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={setRowSelectionModel}
      emptyState={
        <EmptyState
          title="No invoices yet"
          text="Visit the tag shop to place an order"
          icon={<ReceiptTextIcon strokeWidth={1} size={32} />}
          // VRG-425: this was commented out, so the empty state told the user to
          // visit the tag shop and gave them no way to. Same defect as the Tags
          // Inventory empty state, fixed in the same pass.
          buttons={
            <Button
              variant="primary"
              disableElevation
              startIcon={<ShoppingBagIcon strokeWidth={1} />}
              onClick={() => navigate(ROUTES.TAGS_SHOP, { state: referrer })}
            >
              Visit Tag Shop
            </Button>
          }
        />
      }
    />
  );
};
